import { useCallback, useEffect, useMemo, useState } from "react";

const shouldLogProfilePic =
  (typeof import.meta !== "undefined" && Boolean(import.meta.env?.DEV)) ||
  (typeof window !== "undefined" &&
    window.localStorage.getItem("ppLog") === "1");

const logProfilePic = (...args: any[]) => {
  if (!shouldLogProfilePic) return;
  // eslint-disable-next-line no-console
  // console.log("[profile-picture]", ...args);
};

/**
 * Builds a usable profile picture src. Cache-busting is opt-in.
 */
export function buildProfilePictureSrc(
  url: string | undefined | null,
  cacheBust: boolean = false,
  versionToken?: string | number
): string | undefined {
  if (!url) return undefined;
  if (!cacheBust) return url;
  const version = versionToken ?? Date.now();
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}v=${encodeURIComponent(String(version))}`;
}

/**
 * Standardized onError handler: marks the image as broken and clears the src to stop error loops.
 */
export function createProfilePictureErrorHandler(
  setBroken: (broken: boolean) => void
) {
  return (e: any) => {
    setBroken(true);
    // logProfilePic("image error", {
    //   src: (e?.target as HTMLImageElement | undefined)?.src,
    //   error: e?.message ?? e,
    // });
    const target = (e?.target as HTMLImageElement | undefined) ?? undefined;
    if (target) {
      target.onerror = null;
      target.src = "";
    }
  };
}

/**
 * Hook to manage a profile picture src with cache-busting and graceful fallback.
 */
export function useProfilePictureSrc(
  profilePictureUrl: string | undefined | null
) {
  const [broken, setBroken] = useState(false);
  const [version, setVersion] = useState<number | null>(null);

  useEffect(() => {
    setBroken(false);
    setVersion(null);
    // logProfilePic("url changed", { profilePictureUrl });
  }, [profilePictureUrl]);

  const refresh = useCallback(() => setVersion(Date.now()), []);

  const src = useMemo(() => {
    if (!profilePictureUrl || broken) return undefined;
    // Keep a stable URL for browser caching; only bust after explicit refresh().
    return buildProfilePictureSrc(
      profilePictureUrl,
      version !== null,
      version ?? undefined
    );
  }, [profilePictureUrl, broken, version]);

  // useEffect(() => {
  //   logProfilePic("computed src", {
  //     input: profilePictureUrl,
  //     broken,
  //     version,
  //     src,
  //   });
  // }, [profilePictureUrl, broken, version, src]);

  const onError = useMemo(
    () => createProfilePictureErrorHandler(setBroken),
    []
  );

  return { src, onError, isBroken: broken, refresh };
}
